import { useState } from 'react';
import HologramCard from '../../../components/base/HologramCard';
import GlitchText from '../../../components/base/GlitchText';

export default function ProjectsSection() {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  const projects = [
    {
      id: 'cmp-bookpedia',
      title: 'CMP-Bookpedia',
      type: 'RECOMMENDATION ENGINE',
      description: 'Intuitive book discovery experience with community-driven insights, improving decision-making speed and personalized recommendations.',
      tech: ['User Journey Mapping', 'Recommendation Flows', 'Real-time Sync', 'UX Research'],
      github: 'https://github.com/AdityaPrakash-03/CMP-Bookpedia',
      features: [
        'User journey mapping for seamless book discovery',
        'Community-driven recommendation system',
        'Real-time synchronization across devices',
        'Enhanced user feedback integration'
      ],
      color: 'cyan',
      icon: 'ri-book-open-line',
      status: 'DEPLOYED'
    },
    {
      id: 'dreamspace',
      title: 'DreamSpace',
      type: 'AI DESIGN TOOL',
      description: 'AI-powered design tool with Flutter + Flask architecture, featuring real-time image pipelines and UX improvements based on user feedback.',
      tech: ['Flutter', 'Flask', 'AI Integration', 'Real-time Processing'],
      github: 'https://github.com/AdityaPrakash-03/DreamSpace.git',
      features: [
        'AI-powered design generation',
        'Flutter + Flask hybrid architecture',
        'Real-time image processing pipelines',
        'Iterative UX improvements from user feedback'
      ],
      color: 'purple',
      icon: 'ri-palette-line',
      status: 'ACTIVE'
    },
    {
      id: 'habit-tracker',
      title: 'Habit Tracker',
      type: 'PRODUCTIVITY APP',
      description: 'A minimal, user-friendly habit-tracking application designed to help users build consistent routines. Features intuitive daily check-ins, progress visualization, clean UI flows, and a smooth experience built with Flutter—focused on helping users stay disciplined and motivated.',
      tech: ['Flutter', 'Local Storage', 'Daily Tracking', 'Progress Visualization'],
      github: 'https://github.com/AdityaPrakash-03/Habit-Tracker',
      features: [
        'Intuitive daily check-ins for habit tracking',
        'Progress visualization and analytics',
        'Clean UI flows and smooth user experience',
        'Local storage for offline functionality'
      ],
      color: 'green',
      icon: 'ri-calendar-check-line',
      status: 'ACTIVE'
    }
  ];

  return (
    <section id="projects" className="min-h-screen py-20 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white font-orbitron mb-4">
            <GlitchText text="SSD STORAGE" />
          </h2>
          <div className="text-xl text-orange-400 font-mono mb-2">SOLID STATE DRIVE CONTENTS</div>
          <p className="text-gray-400 font-rajdhani text-lg">High-performance project archives and implementations</p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-12">
          {projects.map((project, index) => (
            <HologramCard 
              key={project.id}
              glowColor={project.color}
              onClick={() => setSelectedProject(selectedProject === project.id ? null : project.id)}
              className={`cursor-pointer transition-all duration-300 ${
                selectedProject === project.id ? 'scale-105' : ''
              }`}
            >
              <div className="space-y-4">
                {/* Project Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className={`${project.icon} text-2xl text-${project.color}-400`}></i>
                    <div>
                      <h3 className="text-lg font-bold text-white font-orbitron">{project.title}</h3>
                      <div className="text-xs text-gray-400 font-mono">{project.type}</div>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-mono ${
                    project.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' :
                    project.status === 'DEPLOYED' ? 'bg-cyan-500/20 text-cyan-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {project.status}
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 font-rajdhani text-sm leading-relaxed">
                  {project.description}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech, techIndex) => (
                    <span 
                      key={techIndex}
                      className={`px-2 py-1 rounded text-xs font-mono bg-${project.color}-500/10 text-${project.color}-400 border border-${project.color}-500/30`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Expand/Collapse Indicator */}
                <div className="flex items-center justify-between">
                  <a 
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center space-x-2 text-${project.color}-400 hover:text-${project.color}-300 transition-colors`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-github-line"></i>
                    <span className="text-xs font-mono">VIEW CODE</span>
                  </a>
                  <button className="text-gray-400 hover:text-white transition-colors">
                    <i className={`ri-arrow-${selectedProject === project.id ? 'up' : 'down'}-line`}></i>
                  </button>
                </div>

                {/* Expanded Content */}
                {selectedProject === project.id && (
                  <div className="mt-4 pt-4 border-t border-gray-700 space-y-3 animate-fade-in">
                    <h4 className="text-sm font-bold text-white font-orbitron">KEY FEATURES:</h4>
                    <ul className="space-y-2">
                      {project.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-2">
                          <div className={`w-1.5 h-1.5 bg-${project.color}-400 rounded-full mt-2 flex-shrink-0`}></div>
                          <span className="text-gray-300 font-rajdhani text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </HologramCard>
          ))}
        </div>
      </div>
    </section>
  );
}
